/**
 * CR313: Feature Flags Admin Page
 * 
 * Registry-first, tabbed Layout-Editor IA with dirty-state Save/Reset.
 */

import React, { useState } from 'react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2 } from 'lucide-react';
import { useFeatureFlagsAdmin } from '@/hooks/admin/useFeatureFlagsAdmin';
import { FeatureFlagsHeader, type StatusFilter, type TypeFilter } from '@/components/admin/feature-flags/FeatureFlagsHeader';
import { FeatureFlagsAllTab } from '@/components/admin/feature-flags/FeatureFlagsAllTab';
import { FeatureFlagsRolloutsTab } from '@/components/admin/feature-flags/FeatureFlagsRolloutsTab';
import { FeatureFlagsAuditTab } from '@/components/admin/feature-flags/FeatureFlagsAuditTab';
import type { FeatureFlagCategory } from '@/lib/admin/featureFlagsRegistry';

export default function FeatureFlags() {
  const adminHook = useFeatureFlagsAdmin();
  const [activeTab, setActiveTab] = useState('all');
  const [isSaving, setIsSaving] = useState(false);
  
  // Filter state
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<FeatureFlagCategory | 'all'>('all');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  
  const handleSave = async () => {
    setIsSaving(true);
    try {
      await adminHook.save();
    } finally {
      setIsSaving(false);
    }
  };
  
  if (adminHook.loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AdminLayout>
    );
  }
  
  if (adminHook.error) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <p className="text-destructive font-medium">Failed to load feature flags</p>
            <p className="text-sm text-muted-foreground mt-1">{adminHook.error}</p>
          </div>
        </div>
      </AdminLayout>
    );
  }
  
  return (
    <AdminLayout>
      <div className="h-full flex flex-col">
        {/* Header */}
        <FeatureFlagsHeader
          search={search}
          onSearchChange={setSearch}
          categoryFilter={categoryFilter}
          onCategoryFilterChange={setCategoryFilter}
          typeFilter={typeFilter}
          onTypeFilterChange={setTypeFilter}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          isDirty={adminHook.isDirty}
          isSaving={isSaving}
          onSave={handleSave}
          onReset={adminHook.resetDraft}
        />
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
          <div className="border-b px-6">
            <TabsList className="h-10 bg-transparent p-0 gap-4">
              <TabsTrigger 
                value="all" 
                className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-0 pb-2"
              >
                All Flags
              </TabsTrigger>
              <TabsTrigger 
                value="rollouts"
                className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-0 pb-2"
              >
                Rollouts
              </TabsTrigger>
              <TabsTrigger 
                value="audit"
                className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-0 pb-2"
              >
                Audit Log
              </TabsTrigger>
            </TabsList>
          </div>
          
          <TabsContent value="all" className="flex-1 mt-0">
            <FeatureFlagsAllTab
              adminHook={adminHook}
              search={search}
              categoryFilter={categoryFilter}
              typeFilter={typeFilter}
              statusFilter={statusFilter}
            />
          </TabsContent>
          
          <TabsContent value="rollouts" className="flex-1 mt-0">
            <FeatureFlagsRolloutsTab adminHook={adminHook} />
          </TabsContent>
          
          <TabsContent value="audit" className="flex-1 mt-0">
            <FeatureFlagsAuditTab />
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
